/**
 * VideoTabContent Component
 *
 * Conditional router that renders the correct tab component based on activeTab.
 * isBackendConnected and externalConfigUpdate are provided via VideoGenerationContext.
 */

import { memo, Suspense } from 'react';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import type { VideoTab } from './types';

const SceneTab = lazyWithRetry(() =>
  import('./tabs/SceneTab').then((m) => ({ default: m.SceneTab }))
);
const ImageToVideoTab = lazyWithRetry(() =>
  import('./tabs/ImageToVideoTab').then((m) => ({ default: m.ImageToVideoTab }))
);
const MotionControlTab = lazyWithRetry(() =>
  import('./tabs/MotionControlTab').then((m) => ({ default: m.MotionControlTab }))
);
const TalkingHeadsTab = lazyWithRetry(() =>
  import('./tabs/TalkingHeadsTab').then((m) => ({ default: m.TalkingHeadsTab }))
);
const FullVideoTab = lazyWithRetry(() =>
  import('./tabs/FullVideoTab').then((m) => ({ default: m.FullVideoTab }))
);

interface VideoTabContentProps {
  activeTab: VideoTab;
}

function TabFallback() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
    </div>
  );
}

export const VideoTabContent = memo(function VideoTabContent({ activeTab }: VideoTabContentProps) {
  const renderTab = () => {
    switch (activeTab) {
      case 'scene':
        return <SceneTab />;
      case 'image-to-video':
        return <ImageToVideoTab />;
      case 'motion-control':
        return <MotionControlTab />;
      case 'talking-heads':
        return <TalkingHeadsTab />;
      case 'full-video':
        return <FullVideoTab />;
      default:
        return <SceneTab />;
    }
  };

  return <Suspense fallback={<TabFallback />}>{renderTab()}</Suspense>;
});
